import { useState, useRef, useCallback, useEffect } from 'react';
import { useSearch } from '../hooks/useApi';

interface SearchBarProps {
  onSelect: (type: string, id: string) => void;
  compact?: boolean;
}

export default function SearchBar({ onSelect, compact }: SearchBarProps) {
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query), 300);
    return () => clearTimeout(timer);
  }, [query]);

  const { data, isLoading } = useSearch(debouncedQuery);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleSelect = useCallback((type: string, id: string) => {
    onSelect(type, id);
    setQuery('');
    setIsOpen(false);
  }, [onSelect]);

  const results = data?.data;
  const hasResults = results && ((results.documents?.items?.length || 0) + (results.emails?.items?.length || 0)) > 0;

  return (
    <div className={`search-bar ${compact ? 'compact' : ''}`} ref={containerRef}>
      <div className="search-input-wrapper">
        <svg className="search-icon" viewBox="0 0 20 20" fill="currentColor" width="16" height="16">
          <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
        </svg>
        <input
          ref={inputRef}
          type="text"
          className="search-input"
          placeholder="Search persons, documents, emails..."
          value={query}
          onChange={(e) => { setQuery(e.target.value); setIsOpen(true); }}
          onFocus={() => setIsOpen(true)}
        />
        {isLoading && <div className="search-spinner" />}
      </div>

      {isOpen && debouncedQuery.length >= 2 && (
        <div className="search-dropdown">
          {!hasResults && !isLoading && (
            <div className="search-empty">No results for "{debouncedQuery}"</div>
          )}

          {results?.documents?.items?.length ? (
            <div className="search-section">
              <div className="search-section-header">
                Documents ({results.documents.total.toLocaleString()})
              </div>
              {results.documents.items.slice(0, 5).map((doc) => (
                <button
                  key={doc.id}
                  className="search-result"
                  onClick={() => handleSelect('document', doc.id)}
                >
                  <span className="result-badge doc">DOC</span>
                  <div className="result-text">
                    <div className="result-title">{doc.title}</div>
                    {doc.snippet && <div className="result-snippet">{doc.snippet}</div>}
                  </div>
                </button>
              ))}
            </div>
          ) : null}

          {results?.emails?.items?.length ? (
            <div className="search-section">
              <div className="search-section-header">
                Emails ({results.emails.total.toLocaleString()})
              </div>
              {results.emails.items.slice(0, 5).map((email) => (
                <button
                  key={email.id}
                  className="search-result"
                  onClick={() => handleSelect('email', email.id)}
                >
                  <span className="result-badge email">EMAIL</span>
                  <div className="result-text">
                    <div className="result-title">{email.subject}</div>
                    {email.snippet && <div className="result-snippet">{email.snippet}</div>}
                  </div>
                </button>
              ))}
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}
